/** Admin: switch product features on or off, and list the countries where each is never offered. */
import { useCallback, useEffect, useState } from 'react';
import { FEATURE_KEYS, FEATURE_NAMES, fmtDateTime, type FeatureKey, type FeatureSettings } from '@platform/shared';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg uppercase outline-none focus:border-line-strong placeholder:text-muted placeholder:normal-case';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

const ABOUT: Record<FeatureKey, string> = {
  copy_trading: 'Clients copy lead traders’ trades in proportion to the amount they put in.',
  smart_portfolios: 'Curated baskets of instruments that rebalance automatically.',
  meme: 'Meme coin CFDs on our quoted price (no DEX trades).',
  master_accounts: 'Dealers trade a master account; clients staff assign to it get the same trades, sized to their equity.',
  volume_rewards: 'Earn → Rewards: USDT, trading bonuses and rebates for reaching trading-volume steps. Campaigns are set in Earn program → Volume rewards.',
};

interface Data {
  features: FeatureSettings;
  updatedBy: string | null;
  updatedAt: string | null;
}

type Form = Record<FeatureKey, { enabled: boolean; countries: string }>;
const toForm = (s: FeatureSettings): Form =>
  Object.fromEntries(FEATURE_KEYS.map((k) => [k, { enabled: s[k].enabled, countries: s[k].blockedCountries.join(', ') }])) as Form;

export function FeaturesTab() {
  const [data, setData] = useState<Data | null>(null);
  const [f, setF] = useState<Form | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get<Data>('/api/admin/features');
      setData(d);
      setF(toForm(d.features));
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    }
  }, []);
  useEffect(() => void load(), [load]);

  const save = async () => {
    if (!f) return;
    setSaving(true);
    setMsg(null);
    try {
      const features = Object.fromEntries(
        FEATURE_KEYS.map((k) => [k, { enabled: f[k].enabled, blockedCountries: f[k].countries.split(/[\s,]+/).filter(Boolean) }]),
      );
      await api.put('/api/admin/features', { features });
      await load();
      setMsg({ ok: true, text: 'Saved' });
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setSaving(false);
    }
  };

  if (!f) return msg ? <p className="m-0 text-sm text-down">{msg.text}</p> : <Spinner />;
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <p className="m-0 text-sm text-muted">
        These features stay off until legal sign-off. A feature that is off is hidden from clients and every one of its actions is refused. Countries listed are blocked
        by the client’s residence and by the country they connect from.
      </p>
      <div className="flex flex-col divide-y divide-line rounded-xl border border-line bg-panel">
        {FEATURE_KEYS.map((k) => (
          <div key={k} className="grid gap-3 p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,260px)] md:items-center">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                id={`feature-${k}`}
                type="checkbox"
                checked={f[k].enabled}
                onChange={(e) => setF({ ...f, [k]: { ...f[k], enabled: e.target.checked } })}
                className="mt-1 size-4 accent-[var(--accent)]"
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold">
                  {FEATURE_NAMES[k]}{' '}
                  <span className={cx('ml-1 rounded-full px-2 py-0.5 text-[11px] font-semibold', f[k].enabled ? 'bg-up-bg text-up' : 'bg-raised text-muted')}>{f[k].enabled ? 'On' : 'Off'}</span>
                </span>
                <span className="text-xs text-muted">{ABOUT[k]}</span>
              </span>
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Blocked countries
              <input
                id={`feature-${k}-countries`}
                value={f[k].countries}
                onChange={(e) => setF({ ...f, [k]: { ...f[k], countries: e.target.value } })}
                placeholder="None, e.g. GB, BE"
                className={input}
              />
            </label>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={primary} disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
        {data?.updatedAt && (
          <span className="text-xs text-muted">
            Last changed {fmtDateTime(data.updatedAt)}
            {data.updatedBy ? ` by ${data.updatedBy}` : ''}
          </span>
        )}
      </div>
    </div>
  );
}
